import * as THREE from 'three'
import { StereoEffect } from 'three/examples/jsm/effects/StereoEffect'
import { Scene } from './Scene'
import { CPointSet } from './CameraPoints'
import { Renderer } from './Renderer'
import { Camera } from './Camera'
import { CameraController } from './CameraController'

class VRPoint {
  /**
   * VR提示移动点位，构造函数
   * @param {CameraPoint} CameraPoint 相机点位
   */
  constructor (CameraPoint) {
    /**
     * @type {number} 点位id
     */
    this.id = CameraPoint.id
    /**
     * @type {THREE.Vector3} 点位位置
     */
    this.position = CameraPoint.position
    /**
     * @type {THREE.Mesh} 点位mesh网格
     */
    this.pointMesh = null
    this.initPoint()
  }

  /**
   * 初始化一个点
   */
  initPoint () {
    const vertexShader =
    'uniform float lenY;\n' +
    'varying vec4 vertexColor;\n' +
    'void main(void)\n' +
    '{\n' +
      'vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );\n' +
      'vec4 ppos = projectionMatrix * mvPosition;\n' +
      'gl_Position = ppos;\n' +
      'float weight = ((position.y / lenY)-0.5)*2.0;\n' +
      'vertexColor = mix( vec4( 0.408, 0.718, 1.0, 0.8 ), vec4( 0.086, 0.153, 0.216, 0.8 ),  weight*weight );\n' +
    '}'

    const fragmentShader =
    'varying vec4 vertexColor;\n' +
    'void main(void)\n' +
    '{\n' +
      'gl_FragColor = vertexColor;\n' +
    '}\n'

    const staticG = new THREE.OctahedronBufferGeometry(140, 0)
    staticG.computeBoundingBox()
    // const staticM = new THREE.MeshStandardMaterial({ color: 0xffffff })
    const staticM = new THREE.ShaderMaterial({
      uniforms: {
        lenY: { value: staticG.boundingBox.max.y - staticG.boundingBox.min.y }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true
    })
    this.pointMesh = new THREE.Mesh(staticG, staticM)
    staticG.dispose()
    staticM.dispose()
    this.pointMesh.position.set(
      this.position.x,
      this.position.y - 500,
      this.position.z
    )
  }

  /**
   * 释放一个点
   */
  depose () {
    this.pointMesh.geometry.dispose()
    this.pointMesh.material.dispose()
    this.pointMesh.remove()
  }
}

export class VRControl {
  /**
   * 构造函数
   * @param {Element} dom DOM元素
   */
  constructor (dom) {
    /**
     * @type {boolean} 是否VR模式
     */
    this.vrmode = false
    /**
     * @type {StereoEffect} 效果控件
     */
    this.effect = null
    /**
     * @type {Element} DOM元素
     */
    this.dom = dom
    /**
     * @type {VRPoint} vr点位集
     */
    this.VRPoints = []
    /**
     * @type {[]} vr点位组
     */
    this.vrGroup = null
    /**
     * @type {THREE.Raycaster} 射线
     */
    this.raycaster = null
    /**
     * @type {[]} 射线检测组
     */
    this.intersectObj = []
    /**
     * @type {[]} 射线检测结果组
     */
    this.intersectResult = []
    /**
     * @type {THREE.Mesh} 当前指向mesh
     */
    this.currMesh = null
    /**
     * @type {Function} 更新绑定方法
     */
    this.updateBind = this.update.bind(this)
  }

  /**
   * 初始化方法
   * @param {CPointSet} cps CPS实例
   * @param {THREE.Scene} scene 场景根节点
   * @param {Renderer} renderer 渲染器实例
   * @param {CameraController} controller 控制器实例
   */
  init (cps, scene, renderer, controller) {
    // 初始化VR点位
    this.vrGroup = new THREE.Group()
    for (const p of cps.CameraPoints) {
      const vp = new VRPoint(p)
      this.VRPoints.push(vp)
      this.vrGroup.add(vp.pointMesh)
      this.intersectObj.push(vp.pointMesh)
    }
    scene.add(this.vrGroup)
    this.vrGroup.visible = false

    /**
     * @type {Renderer} 渲染器
     */
    this._renderer = renderer
    /**
     * @type {CameraController} 渲染器
     */
    this._controller = controller

    // 设置stereoEffect效果
    this.effect = new StereoEffect(this._renderer.renderer)
    this.effect.setSize(this.dom.clientWidth, this.dom.clientHeight)
    this.effect.domElement = this._renderer.renderer.domElement

    // 初始化射线
    this.raycaster = new THREE.Raycaster()
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), this._controller.camera.camera)
    this.resizeFunction = this.onWindowResize.bind(this)
    // 添加window resize
  }

  /**
   * 窗口调整方法
   */
  onWindowResize () {
    this.removeCollimator()
    this.createCollimator()
  }

  /**
   * 进入VR
   */
  onVR () {
    this.vrGroup.visible = true
    this.originRenderer = this._renderer.renderer
    this._renderer.renderer = this.effect
    this.createCollimator()
    document.getElementsByClassName('hud-container')[0].style.display = 'none' // TODO
    this.vrmode = true
    window.addEventListener('resize', this.resizeFunction)
  }

  /**
   * 离开VR
   */
  offVR () {
    this.vrGroup.visible = false
    this._renderer.renderer = this.originRenderer
    const size = this.originRenderer.getSize()
    this._renderer.renderer.setViewport(0, 0, size.width, size.height)
    this.removeCollimator()
    document.getElementsByClassName('hud-container')[0].style.display = 'block'
    document.getElementsByClassName('hud-container')[0].style.opacity = '1'
    this.vrmode = false
    window.removeEventListener('resize', this.resizeFunction)
  }

  /**
   * 创建准星
   */
  createCollimator () {
    const collimator = document.createElement('canvas')
    collimator.width = this.dom.clientWidth
    collimator.height = this.dom.clientHeight
    collimator.setAttribute('id', 'collimator')
    collimator.style.pointerEvents = 'none'
    collimator.style.opacity = '0.8'
    collimator.style.position = 'fixed'
    this.ctx = collimator.getContext('2d')
    // 绘制
    this.initCollimator(this.ctx)
    // 添加
    document.getElementById('model-demo').insertBefore(collimator, this.dom)
  }

  /**
   * 绘制准星
   * @param {} ctx canvas内容
   */
  initCollimator (ctx) {
    ctx.clearRect(0, 0, this.dom.clientWidth, this.dom.clientHeight)
    ctx.save()
    ctx.fillStyle = 'rgb(255,255,255)'
    ctx.beginPath()
    ctx.arc(this.dom.clientWidth / 4, this.dom.clientHeight / 2, 4, 0, Math.PI * 2, false)
    ctx.fill()
    ctx.beginPath()
    ctx.arc(this.dom.clientWidth * 3 / 4, this.dom.clientHeight / 2, 4, 0, Math.PI * 2, false)
    ctx.fill()
    ctx.restore()
  }

  /**
   * 移除准星
   */
  removeCollimator () {
    document.getElementById('model-demo').removeChild(document.getElementById('collimator'))
  }

  /**
   * 更新准星
   * @param {} ctx canvas内容
   */
  updateCollimator (ctx) {
    ctx.clearRect(0, 0, this.dom.clientWidth, this.dom.clientHeight)
    ctx.save()
    ctx.fillStyle = 'rgb(0,255,255)'
    ctx.beginPath()
    ctx.arc(this.dom.clientWidth / 4, this.dom.clientHeight / 2, 2, 0, Math.PI * 2, false)
    ctx.fill()
    ctx.beginPath()
    ctx.arc(this.dom.clientWidth * 3 / 4, this.dom.clientHeight / 2, 2, 0, Math.PI * 2, false)
    ctx.fill()

    ctx.lineWidth = 2
    ctx.strokeStyle = 'rgb(255,255,255)'
    ctx.beginPath()
    ctx.arc(this.dom.clientWidth / 4, this.dom.clientHeight / 2, 6, 0, Math.PI * 2, false)
    ctx.stroke()
    ctx.beginPath()
    ctx.arc(this.dom.clientWidth * 3 / 4, this.dom.clientHeight / 2, 6, 0, Math.PI * 2, false)
    ctx.stroke()

    ctx.strokeStyle = 'rgb(0,255,255)'
    ctx.beginPath()
    ctx.arc(this.dom.clientWidth / 4, this.dom.clientHeight / 2, 10, 0, Math.PI * this.counter / 60, false)
    ctx.stroke()
    ctx.beginPath()
    ctx.arc(this.dom.clientWidth * 3 / 4, this.dom.clientHeight / 2, 10, 0, Math.PI * this.counter / 60, false)
    ctx.stroke()

    ctx.restore()
  }

  /**
   * 检测鼠标射线与点位编辑图标相交，获取目标点位信息。
   * @param {THREE.Raycaster} raycaster 鼠标射线检测与编辑点位贴图相交
   * @returns {THREE.Mesh} 返回第一个与射线相交的对象网格，若无则为null
   */
  interact (raycaster) {
    if (!this.vrmode) return
    if (this.vrGroup.visible) {
      this.intersectResult.length = 0
      raycaster.intersectObjects(this.intersectObj, false, this.intersectResult)
      if (this.intersectResult.length > 0) {
        return this.intersectResult[0]
      } else return null
    } else return null
  }

  /**
   * 重力感应移动事件
   * @param {THREE.Mesh} mesh 目标跳转点的mesh
   */
  onMove (mesh) {
    if (!this.vrmode) return
    for (const vrp of this.VRPoints) {
      if (vrp.pointMesh === mesh) {
        console.log('vr目标跳转点位是', vrp.id)
        this._controller.animateCamera(this._controller.camera.camera.position, vrp.id) // 走动过程正常来说是2秒(2000*speedratio=1)
        return
      }
    }
    console.warn('找不到目标点位')
  }

  /**
   * 更新方法
   */
  update () {
    if (this.vrmode && !this._controller.functionLock) {
      // 更新射线，检测相交点位，更新状态。
      this.raycaster.setFromCamera(new THREE.Vector2(0, 0), this._controller.camera.camera)
      const rayUI = this.interact(this.raycaster)
      if (rayUI === null) {
        if (this.currMesh !== null) {
          this.currMesh = null
          this.initCollimator(this.ctx)
          this.counter = 0
        }
        return
      }
      const tempMesh = rayUI.object
      if (tempMesh !== null && tempMesh === this.currMesh) {
        // 仅有射线mesh不为null且仍等于当前mesh
        this.counter++
        if (this.counter % 3 === 0) {
          this.updateCollimator(this.ctx)
        }
        if (this.counter >= 120) { // 维持120帧后(每秒60帧)
          this.counter = 0
          this.onMove(this.currMesh)
          this.initCollimator(this.ctx)
        }
      } else {
        // 其余情况：射线mesh仍为空(?) 或者 射线mesh与当前mesh不一致
        this.currMesh = tempMesh
        this.counter = 1 // 计数器置1
      }
    }
  }

  /**
   * 资源释放方法
   */
  depose () {
    // 释放资源方法
    this.effect = null
    this.raycaster = null
    this.currMesh = null
    for (const vrp of this.VRPoints) {
      vrp.depose()
    }
  }
}
